import { Box, Col, Row, VStack } from "@reverb-ui/react";
import { panelsInfo } from "helper/panelsHelper";
import { ListCardSkeleton } from "presentation/components/Skeleton/CardSkeleton";
import { SortedTableSkeleton } from "presentation/components/Skeleton/SortedTableSkeleton";
import { SortedTable } from "presentation/components/SortedTable";
import { useContext, useMemo } from "react";
import { MetricCard } from "../../components/MetricCard";
import { PageSpeedContext } from "../context-api/PageSpeedProvider";
import { PageSpeedCell } from "./PageSpeedCell";
import PanelContainer from "presentation/components/PanelContainer";

const getIconSpeed = (
  scoreNumber: number
): { iconName: string; color: string } => {
  if (scoreNumber >= 80) {
    return { iconName: "Star", color: "primary.500" };
  }

  if (scoreNumber >= 50) {
    return { iconName: "ThumbsUp", color: "success.500" };
  }

  return { iconName: "ThumbsDown", color: "danger.500" };
};

const getPageSpeedCell = (children: string, scoreNumber: number) => {
  return (
    <PageSpeedCell {...getIconSpeed(scoreNumber)}>{children}</PageSpeedCell>
  );
};

const pageSpeedInfo = panelsInfo.pageSpeed;

export const PageSpeedReports = () => {
  const {
    requestParamsIndex,
    pageSpeedData,
    downloadPageSpeedReport,
    downloadingReports,
    isLoading,
  } = useContext(PageSpeedContext);

  const { speeds } = pageSpeedData || {};

  const pageSpeedRow = useMemo(() => {
    return speeds?.dataRows.map((pageSpeed) => ({
      pageName: pageSpeed.pageName,
      reportDate: pageSpeed.reportDate,
      desktopScoreSpeed: getPageSpeedCell(
        pageSpeed.desktopScoreSpeed,
        pageSpeed.desktopScore
      ),
      mobileScoreSpeed: getPageSpeedCell(
        pageSpeed.mobileScoreSpeed,
        pageSpeed.mobileScore
      ),
    }));
  }, [speeds]);

  return (
    <PanelContainer
      panelId={pageSpeedInfo.htmlId}
      panelName={pageSpeedInfo.description}
      isLoading={isLoading}
      downloadOptions={{
        onClickGenerateCSV: downloadPageSpeedReport,
        downloadingReports: downloadingReports,
        panelInfo: pageSpeedInfo,
        filterStateIndex: requestParamsIndex,
      }}
    >
      <Row>
        <Col col={!isLoading && pageSpeedData?.cards.length === 0 ? 0 : 3}>
          {isLoading ? (
            <ListCardSkeleton
              numberOfCards={2}
              spacingBetween={5}
              hasSubText={true}
            />
          ) : (
            <VStack w="full" spacing={5}>
              {pageSpeedData?.cards.map((item, index) => (
                <MetricCard
                  key={index}
                  order={item.order}
                  iconName={item.iconTitle}
                  title={item.title}
                  description={item.text}
                  subDescription={item.subText}
                />
              ))}
            </VStack>
          )}
        </Col>
        <Col col={!isLoading && pageSpeedData?.cards.length === 0 ? 12 : 9}>
          <Box
            bg="white"
            w="full"
            p={6}
            height={400}
            overflow="auto"
            className="sortedTable"
          >
            {isLoading ? (
              <SortedTableSkeleton numberOfColumns={4} />
            ) : (
              <SortedTable
                dataColumns={pageSpeedData?.speeds.dataColumns || []}
                dataRows={pageSpeedRow || []}
              />
            )}
          </Box>
        </Col>
      </Row>
    </PanelContainer>
  );
};
